/**
 * UserDetailPage — View/edit a user's plant access permissions.
 * Route: /administration/users/:userId?sam=...
 *
 * Shows user info header + user settings + SecurityTreePanel for managing access.
 * The SAM account name from the URL is used to query/save the security tree and DB-backed user settings.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useSafeMutation } from '../../api/useSafeMutation';
import { graphql } from '../../gql';
import { SecurityTreePanel } from '../../components/security-tree/SecurityTreePanel';
import { PageHeader, InfoCard, StatusChip, SelectInput, Checkbox, LoadingOverlay } from '../../components/ui';
import type { PlantAccessEntry } from '../../components/security-tree/types';

const GET_SECURITY_TREE = graphql(`
  query GetSecurityTree($userName: String!) {
    securityTree(userName: $userName) {
      id
      originId
      nodeType
      label
      parentId
      accessState
    }
  }
`);

const GET_USER_FAIM_GROUPS = graphql(`
  query GetUserFaimGroups($userName: String!) {
    userFaimGroups(userName: $userName)
  }
`);

const GET_USER_ADMINISTRATION_DATA = graphql(`
  query GetUserAdministrationData($userName: String!) {
    userAccessSettings(userName: $userName) {
      cultureId
      fleetManager
      financialAccess
      maintenanceAccess
    }
    localizationCultures {
      id
      code
      name
    }
  }
`);

const SET_USER_PLANT_ACCESS = graphql(`
  mutation SetUserPlantAccess($input: SetUserPlantAccessInput!) {
    setUserPlantAccess(input: $input) {
      success
      affectedCount
    }
  }
`);

interface UserSettingsState {
  cultureId: number;
  fleetManager: boolean;
  financialAccess: boolean;
  maintenanceAccess: boolean;
}

export function UserDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sam = searchParams.get('sam') ?? '';
  const displayName = searchParams.get('name') ?? sam;
  const email = searchParams.get('email') ?? '';
  const accountEnabled = searchParams.get('enabled') !== 'false';

  const { data, loading, error, refetch } = useQuery(GET_SECURITY_TREE, {
    variables: { userName: sam },
    skip: !sam,
  });

  const { data: groupsData, loading: groupsLoading } = useQuery(GET_USER_FAIM_GROUPS, {
    variables: { userName: sam },
    skip: !sam,
  });

  const {
    data: adminData,
    loading: adminLoading,
    refetch: refetchAdminData,
  } = useQuery(GET_USER_ADMINISTRATION_DATA, {
    variables: { userName: sam },
    skip: !sam,
  });

  const [saveAccess, { loading: saving }] = useSafeMutation(SET_USER_PLANT_ACCESS);
  const [settings, setSettings] = useState<UserSettingsState | null>(null);
  const [initialSettings, setInitialSettings] = useState<UserSettingsState | null>(null);

  const pageLoading = loading || groupsLoading || adminLoading || saving;

  const treeData = data?.securityTree ?? undefined;

  useEffect(() => {
    const accessSettings = adminData?.userAccessSettings;
    if (!accessSettings) {
      return;
    }

    const nextSettings = {
      cultureId: accessSettings.cultureId,
      fleetManager: accessSettings.fleetManager,
      financialAccess: accessSettings.financialAccess,
      maintenanceAccess: accessSettings.maintenanceAccess,
    };

    setSettings(nextSettings);
    setInitialSettings(nextSettings);
  }, [adminData]);

  const settingsDirty = useMemo(() => {
    if (!settings || !initialSettings) {
      return false;
    }

    return settings.cultureId !== initialSettings.cultureId
      || settings.fleetManager !== initialSettings.fleetManager
      || settings.financialAccess !== initialSettings.financialAccess
      || settings.maintenanceAccess !== initialSettings.maintenanceAccess;
  }, [initialSettings, settings]);

  const visiblePlantIds = useMemo(() => {
    const ids = new Set<number>();
    for (const node of treeData ?? []) {
      if (String(node.nodeType).toLowerCase() === 'plant') {
        ids.add(node.originId);
      }
    }

    return [...ids];
  }, [treeData]);

  const handleSave = async (entries: PlantAccessEntry[]) => {
    if (!settings) {
      return;
    }

    await saveAccess({
      variables: {
        input: {
          userName: sam,
          fullName: displayName,
          email,
          cultureId: settings.cultureId,
          fleetManager: settings.fleetManager,
          financialAccess: settings.financialAccess,
          maintenanceAccess: settings.maintenanceAccess,
          accountEnabled,
          visiblePlantIds,
          plantAccesses: entries.map(e => ({
            plantId: e.plantId,
            accessLevel: e.accessLevel,
          })),
        },
      },
    });

    await Promise.all([refetch(), refetchAdminData()]);
  };

  const handleCancelSettings = () => {
    if (initialSettings) {
      setSettings(initialSettings);
    }
  };

  return (
    <>
      <LoadingOverlay visible={pageLoading} />

      <main className="max-w-[1440px] mx-auto px-6 py-8">
        {/* Header */}
        <PageHeader
          title="User Access Permissions"
          subtitle={displayName}
          secondaryText={sam}
          backLabel="Back to User Search"
          onBack={() => navigate('/administration/users')}
        />

        {/* Loading */}
        {loading && <p className="text-sm text-foreground-faded">Loading security tree...</p>}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
            {error.message}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            {!loading && (
              <SecurityTreePanel
                data={treeData}
                onSave={handleSave}
                externalDirty={settingsDirty}
                onExternalCancel={handleCancelSettings}
                saving={saving}
                treeHeight={600}
              />
            )}
          </div>

          <aside className="space-y-6">
            <InfoCard title="User Info">
              <dl className="space-y-3 text-sm">
                <div>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{email || 'No email available'}</Typography>
                </div>
                <div>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <StatusChip active={accountEnabled} />
                </div>
              </dl>
            </InfoCard>

            <InfoCard title="AD Groups">
              {groupsLoading && <Typography variant="body2" color="text.secondary">Loading groups...</Typography>}
              {!groupsLoading && (
                <div className="flex gap-2 flex-wrap">
                  {(groupsData?.userFaimGroups?.length ?? 0) === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No FAIM groups assigned</Typography>
                  )}
                  {groupsData?.userFaimGroups?.map((group) => (
                    <Chip
                      key={group}
                      label={group}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </div>
              )}
            </InfoCard>

            <InfoCard title="User Settings">
              {adminLoading || !settings ? (
                <Typography variant="body2" color="text.secondary">Loading settings...</Typography>
              ) : (
                <div className="space-y-3">
                  <SelectInput
                    label="Locale"
                    value={settings.cultureId}
                    onChange={(event) => setSettings({ ...settings, cultureId: Number(event.target.value) })}
                    options={(adminData?.localizationCultures ?? []).map((c) => ({
                      value: c.id,
                      label: `${c.code} (${c.name})`,
                    }))}
                  />

                  <Checkbox
                    label="Financial Access"
                    checked={settings.financialAccess}
                    onChange={(val) => setSettings({ ...settings, financialAccess: val })}
                  />

                  <Checkbox
                    label="Fleet Manager"
                    checked={settings.fleetManager}
                    onChange={(val) => setSettings({ ...settings, fleetManager: val })}
                  />

                  <Checkbox
                    label="Maintenance Access"
                    checked={settings.maintenanceAccess}
                    onChange={(val) => setSettings({ ...settings, maintenanceAccess: val })}
                  />
                </div>
              )}
            </InfoCard>
          </aside>
        </div>
      </main>
    </>
  );
}
